import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes'
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Dialog, dialogClasses, IconButton, Paper, Portal, styled, Theme, Typography } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'
import { visuallyHidden } from '@mui/utils'
import { useDndMonitor, useDraggable } from '@dnd-kit/core'
import { Coordinates } from '@dnd-kit/utilities'
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { focusStyle } from '@app/theme'
import { getDialogPositionPoints, getPositionStyles, createDragHandleListeners } from './helpers'
import { BasicDraggableDialogProps, StyledDialogProps } from './types'

const defaultCoordinates = {
  x: 10,
  y: 10,
}

// a11y: dnd-kit's KeyboardSensor calls preventDefault on Enter/Space anywhere in the draggable
// Paper (no activatorNode is set), which would otherwise block this native button. Stop those
// keys from bubbling so the button activates normally.
const stopDragActivationKeys = (event: React.KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
}

const BasicDraggableDialog: React.FC<BasicDraggableDialogProps> = ({
  id,
  open,
  title,
  dialogProps = {},
  disableBackdropClose = false,
  customDefaultCoordinates,
  onClose,
  paperStyles = {},
  position = 'right-top',
  enableHeader = true,
  requireDragHandle = false,
  nonModal = false,
  hasDialogRole = false,
  hasBackground = false,
  onExited,
  children,
}) => {
  const {
    attributes,
    listeners: draggableListeners,
    setNodeRef,
    transform,
  } = useDraggable({
    id,
  })

  const listeners = useMemo(
    () => createDragHandleListeners(draggableListeners, requireDragHandle),
    [draggableListeners, requireDragHandle],
  )

  const [{ x, y }, setCoordinates] = useState<Coordinates>(customDefaultCoordinates || defaultCoordinates)
  const isDraggingRef = useRef(false)

  useDndMonitor({
    onDragStart: ({ active }) => {
      if (active?.id === id) isDraggingRef.current = true
    },
    onDragCancel: () => {
      isDraggingRef.current = false
    },
    onDragEnd: ({ delta, active }) => {
      isDraggingRef.current = false
      if (active?.id !== id) return

      setCoordinates(({ x, y }) => {
        return getDialogPositionPoints(position, x, y, delta)
      })
    },
  })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined

  const positionStyles = useMemo(() => getPositionStyles(position, x, y), [position, x, y])

  const onExitedRef = useRef(onExited)
  onExitedRef.current = onExited
  const startCoordinatesRef = useRef(customDefaultCoordinates || defaultCoordinates)
  startCoordinatesRef.current = customDefaultCoordinates || defaultCoordinates

  // a11y: the non-modal branch is a bare Portal+Paper with no MUI focus management, so restore focus
  // to the trigger on close — otherwise closing the toolbar drops focus to <body> mid-test (Ready#7707).
  // Only when focus went down with the panel; a control outside it that closed the panel keeps focus.
  useEffect(() => {
    if (!nonModal || !open) return undefined
    const trigger = document.activeElement as HTMLElement | null
    return () => {
      // A keyboard drag cut short by an outside close never gets dnd-kit's cancel, so clear it here.
      isDraggingRef.current = false
      const isFocusLost = !document.activeElement || document.activeElement === document.body
      if (isFocusLost && trigger && document.contains(trigger)) trigger.focus({ preventScroll: true })
      onExitedRef.current?.()
    }
  }, [nonModal, open])

  const content = (
    <>
      {enableHeader && (
        <StyledDialogHeader tabIndex={0}>
          <Typography variant='h5' component='h2'>{title}</Typography>
          <StyledIconButton onClick={onClose} onKeyDown={stopDragActivationKeys} aria-label='Close'>
            <StyledFaTimesIcon icon={faTimes} aria-hidden='true' />
          </StyledIconButton>
        </StyledDialogHeader>
      )}
      {children}
    </>
  )

  const isDialogPanel = hasDialogRole && nonModal
  const titleId = `${id}-title`
  const scrollAreaRef = useRef<HTMLDivElement | null>(null)
  const savedScrollTop = useRef(0)
  const { onKeyDown: dragKeyDown, ...pointerDragListeners } = listeners ?? {}

  // MUI's Portal mounts the panel a render after open, so restore and focus it once it attaches.
  const setPaperRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node)
      if (!node) return
      // A spot saved on a wider screen can be off-screen after a rotate or resize, so start over.
      const { left, top, right, bottom } = node.getBoundingClientRect()
      const isOffScreen = left < 0 || top < 0 || right > window.innerWidth || bottom > window.innerHeight
      if (isOffScreen) setCoordinates(startCoordinatesRef.current)
      if (scrollAreaRef.current) scrollAreaRef.current.scrollTop = savedScrollTop.current
      // a11y: move focus into the panel so screen readers announce the dialog's name.
      node.focus({ preventScroll: true })
    },
    [setNodeRef],
  )

  const panelSx: SystemStyleObject<Theme> = {
    ...positionStyles,
    ...(hasBackground
      ? {
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: 4,
          p: 3,
        }
      : {}),
    ...(isDialogPanel
      ? {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 96px)',
          zIndex: 'modal',
        }
      : {}),
    ...style,
    ...paperStyles,
  }

  const dialogPanelContent = (
    <>
      <StyledDialogHeader data-drag-handle='true' {...pointerDragListeners}>
        <Typography id={titleId} variant='h5' component='h2'>
          {title}
        </Typography>
        <Box display='flex' alignItems='center'>
          <StyledIconButton
            {...attributes}
            onKeyDown={dragKeyDown as React.KeyboardEventHandler<HTMLButtonElement>}
            aria-label={`Move ${title}`}
            data-automation-id={`${id}-move`}
            // a11y: keyboard-only move control; mouse users drag the title bar, so it shows only while focused.
            sx={{ cursor: 'grab', '&:not(:focus)': visuallyHidden }}
          >
            <StyledFaTimesIcon icon={faGripVertical} aria-hidden='true' />
          </StyledIconButton>
          <StyledIconButton onClick={onClose} aria-label={`Close ${title}`}>
            <StyledFaTimesIcon icon={faTimes} aria-hidden='true' />
          </StyledIconButton>
        </Box>
      </StyledDialogHeader>
      <ScrollArea
        ref={scrollAreaRef}
        tabIndex={0}
        role='region'
        aria-label={`${title} content`}
        data-automation-id={`${id}-content`}
        onScroll={(event: React.UIEvent<HTMLDivElement>) => {
          savedScrollTop.current = event.currentTarget.scrollTop
        }}
      >
        {children}
      </ScrollArea>
    </>
  )

  // a11y: render a plain portaled Paper (no MUI Modal) so the page stays reachable to screen readers
  // while the panel is open. Keeps dnd-kit keyboard drag + Escape-to-close.
  if (nonModal) {
    if (!open) return null

    const handlePaperKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Escape mid keyboard-drag cancels the drag; it shouldn't also close the panel.
        if (!isDraggingRef.current) onClose()
        return
      }
      if (isDialogPanel) return
      const dragKeyDown = listeners?.onKeyDown as ((e: React.KeyboardEvent) => void) | undefined
      dragKeyDown?.(event)
    }

    return (
      <Portal>
        {isDialogPanel ? (
          <Paper
            ref={setPaperRef}
            role='dialog'
            aria-labelledby={titleId}
            tabIndex={-1}
            data-automation-id={id}
            onKeyDown={handlePaperKeyDown}
            sx={panelSx}
          >
            {dialogPanelContent}
          </Paper>
        ) : (
          <Paper
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            onKeyDown={handlePaperKeyDown}
            tabIndex={0}
            sx={panelSx}
          >
            {content}
          </Paper>
        )}
      </Portal>
    )
  }

  return (
    <StyledDialog
      open={open}
      onClose={(_event, reason) => {
        // a11y: keep Escape-to-close working (SC 2.1.2); only backdrop clicks are suppressed
        if (disableBackdropClose && reason === 'backdropClick') return
        onClose()
      }}
      disableEnforceFocus
      hideBackdrop
      TransitionProps={{
        onExited: () => {
          setCoordinates(customDefaultCoordinates || defaultCoordinates)
          onExited?.()
        },
      }}
      sx={{
        '& .MuiDialog-paper': panelSx,
      }}
      PaperProps={{
        ref: setNodeRef,
        ...attributes,
        ...listeners,
        tabIndex: 0,
      }}
      {...dialogProps}
    >
      {content}
    </StyledDialog>
  )
}

const DraggableDialog: React.FC<BasicDraggableDialogProps> = props => {
  const activationConstraint = {
    distance: 5,
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint,
    }),
    useSensor(TouchSensor, {
      activationConstraint,
    }),
    useSensor(KeyboardSensor),
  )
  return (
    <DndContext sensors={sensors} modifiers={[restrictToWindowEdges]}>
      <BasicDraggableDialog {...props} />
    </DndContext>
  )
}

const StyledDialog = styled(Dialog)<StyledDialogProps>(({ dialogStyles }) => ({
  ...dialogStyles,
  maxHeight: 'max-content',
  left: 'unset',
  bottom: 'unset',

  [`& .${dialogClasses.container}`]: {
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    position: 'relative',
  },
}))

const StyledDialogHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: theme.palette.grey[50],
  borderRadius: '6px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: 'grab',

  ':focus-visible': {
    ...focusStyle(theme).border,
  },
}))

const ScrollArea = styled(Box)(({ theme }) => ({
  // a11y: contain visually hidden (absolute) text, or VoiceOver scrolls the whole panel to reach it.
  position: 'relative',
  overflowY: 'auto',
  minHeight: 0,
  paddingRight: theme.spacing(2),
  scrollbarWidth: 'auto',
  scrollbarColor: 'auto',

  // Styled scrollbars stay visible in Chrome and Safari instead of hiding until the user scrolls.
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.grey[50],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[500],
    borderRadius: '4px',
  },

  ':focus-visible': {
    ...theme.mixins.focusRing.shadow,
  },
}))

const StyledIconButton = styled(IconButton)(() => ({
  width: '2.25rem',
  height: '2.25rem',
  cursor: 'pointer',
}))

const StyledFaTimesIcon = styled(FontAwesomeIcon)(({ theme }) => ({
  color: theme.palette.additionalColors.bg_500,
  fontSize: '0.875rem',
}))

export default DraggableDialog
